/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { useEffect, useState } from 'react';
import Avatar from '../avatar';
import { FileDropZone } from '../files/FileDropZone';
import { DropzoneOptions, FileRejection } from 'react-dropzone';

const containerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

interface PreviewParams {
  defaultPicture?: string;
  files?: { preview: string }[];
  width?: string;
  onClearFiles?: () => void;
}

export type PreviewFn = (props: PreviewParams) => JSX.Element;

const preview: PreviewFn = ({defaultPicture, files, width}) => {
  if (!Array.isArray(files) || files.length === 0) {
    return (
      <div style={containerStyle}>
        <Avatar width={width ? width : '100px'} style={{ marginRight: '10px' }}>
          {defaultPicture ? <img alt='avatar' src={defaultPicture} /> : <></>}
        </Avatar>
        <div className='btn btn-link px-3 py-2 rounded ml-2'>
          Update Profile Image
        </div>
      </div>
    );
  }

  const [file] = files;
  return (
    <div style={containerStyle}>
      <Avatar width={width ? width : '100px'} >
        {file ? <img alt='avatar' src={file.preview} /> : <></>}
      </Avatar>
    </div>
  );
};

interface ProfilePictureProps extends Pick<DropzoneOptions, 'maxSize'>{
  handleFilesAdded: <T extends File>(acceptedFiles: T[], fileRejections: FileRejection[]) => void;
  width?: string;
  defaultPicture?: string;
  previewRenderer?: PreviewFn;
}

export default function ProfilePicture(props: ProfilePictureProps) {
  const { handleFilesAdded, defaultPicture, width, previewRenderer = preview } = props;
  const [files, setFiles] = useState<{ preview: string }[]>([]);

  useEffect(() => () => {
    // Make sure to revoke the data uris to avoid memory leaks
    files.forEach(file => URL.revokeObjectURL(file.preview));
  }, [files]);

  const onDrop = (files: File[], fileRejections: FileRejection[]) => {
    setFiles(files.map(file => Object.assign(file, {
      preview: URL.createObjectURL(file)
    })));
    handleFilesAdded(files, fileRejections);
  }

  const onClearFiles = () => {
    setFiles([]);
    handleFilesAdded([], []);
  }

  return <div className='ProfilePicture'>
    <FileDropZone
      multiple={false}
      accept={{
        'image/*': []
      }}
      onDrop={onDrop}
      {...props}
    >
      {previewRenderer({ defaultPicture, files, width, onClearFiles})}
    </FileDropZone>
  </div>;
};

